// Welcome + Steps 1–6 with the footer (Back / Save and finish later / next). No uploads here:
// staff open the upload slots on the status page after the Discovery Session.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Banner, Button, cx } from '../ui';
import { useIntake } from './IntakeContext';
import { IntakeLayout } from './IntakeLayout';
import { nextLabel, REVIEW_STEP } from './logic';
import { Welcome } from './steps/Welcome';
import { StepFirm } from './steps/StepFirm';
import { StepTools } from './steps/StepTools';
import { StepClients } from './steps/StepClients';
import { StepDocs } from './steps/StepDocs';
import { StepQuestions } from './steps/StepQuestions';
import { StepReview } from './steps/StepReview';
import type { ErrorMap } from './steps/common';

const clampStep = (n: number) => Math.max(0, Math.min(REVIEW_STEP, Math.floor(n)));

export function IntakeFlow() {
  const intake = useIntake();
  const { state, issues, flush, touchStep, submitted, editable, fatal } = intake;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('step');
  const step = fromUrl !== null && !Number.isNaN(Number(fromUrl)) ? clampStep(Number(fromUrl)) : clampStep(submitted ? REVIEW_STEP : state.currentStep);
  const [maxReached, setMaxReached] = useState(() => Math.max(step, clampStep(submitted ? REVIEW_STEP : state.currentStep)));
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [triedTools, setTriedTools] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => { setMaxReached((m) => Math.max(m, step)); }, [step]);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    try { window.scrollTo(0, 0); } catch { /* jsdom */ }
    document.querySelector<HTMLElement>('[data-step-heading]')?.focus({ preventScroll: true });
  }, [step]);

  const go = useCallback((n: number) => {
    setSaved(false);
    setProblem(null);
    setParams({ step: String(clampStep(n)) });
  }, [setParams]);

  // Before a submit attempt, only an Other left untyped shows inline (and holds Continue on step 2);
  // answers that are required to submit stay soft until Submit.
  const errorsFor = useCallback((n: number): ErrorMap => {
    const shown = issues.filter((i) => i.severity === 'error' && i.step === n && (showErrors || (n === 2 && triedTools && i.code === 'other_required')));
    return Object.fromEntries(shown.map((i) => [i.path, i.message]));
  }, [issues, showErrors, triedTools]);

  const label = nextLabel(step, submitted);

  const next = async () => {
    setProblem(null);
    if (step === 0) { go(1); return; }
    if (step === 2) {
      setTriedTools(true);
      const blocking = issues.filter((i) => i.step === 2 && i.code === 'other_required');
      if (blocking.length) {
        const fn = blocking[0]!.path.split('.')[1];
        document.querySelector<HTMLInputElement>(`[data-testid="tool-row-${fn}"] input[type=text]`)?.focus();
        return;
      }
    }
    if (step >= 1 && step < REVIEW_STEP) { if (editable) touchStep(step); go(step + 1); return; }
    // Review and submit
    setBusy(true);
    try {
      const ok = await flush();
      if (!ok) { setProblem('We couldn’t save your latest changes. Check your connection and try again.'); return; }
      if (submitted) { navigate('/onboard/status'); return; }
      await api.post('/api/intake/submit', {});
      intake.markSubmitted();
      navigate('/onboard/book');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'validation_failed') {
        setShowErrors(true);
        const first = e.issues.find((i) => i.severity === 'error');
        if (first) go(first.step);
      } else {
        setProblem(e instanceof ApiError && e.message ? e.message : 'Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const saveLater = async () => {
    setBusy(true);
    const ok = await flush();
    setBusy(false);
    if (ok) setSaved(true); else setProblem('We couldn’t save your latest changes. We’ll keep trying.');
  };

  const stepErrors = useMemo(() => errorsFor(step), [errorsFor, step]);
  const stepErrorCount = Object.keys(stepErrors).length;

  const body = (() => {
    switch (step) {
      case 0: return <Welcome />;
      case 1: return <StepFirm errors={stepErrors} />;
      case 2: return <StepTools errors={stepErrors} flagUnanswered={triedTools || showErrors || maxReached >= REVIEW_STEP} />;
      case 3: return <StepClients errors={stepErrors} />;
      case 4: return <StepDocs />;
      case 5: return <StepQuestions />;
      default: return <StepReview onEdit={go} attempted={showErrors} />;
    }
  })();

  return (
    <IntakeLayout step={step} maxReached={maxReached} onGo={go}>
      {saved && (
        <Banner tone="success" role="status">
          Your progress is saved. Open the link in your invitation email anytime in the next 30 days to pick up where you left off.
        </Banner>
      )}
      {fatal && <Banner tone="error" role="alert">{fatal}</Banner>}
      {!editable && step > 0 && (
        <Banner tone="info">Your answers are locked now that your Discovery Session has been held. Contact your Customer Experience Manager if something needs to change.</Banner>
      )}
      {stepErrorCount > 0 && step !== REVIEW_STEP && (
        <Banner tone="error" role="alert">
          {step !== 2 ? 'A few answers on this step are needed before you can submit.'
            : Object.keys(stepErrors).some((k) => k.endsWith('.other')) ? 'Type the name of each other tool you selected.'
            : 'Answer every tool question before you submit: pick your tools, Other or Not applicable.'}
        </Banner>
      )}
      <fieldset disabled={!editable && step !== 0 && step !== REVIEW_STEP} className="m-0 min-w-0 border-0 p-0">
        {body}
      </fieldset>
      {problem && <Banner tone="error" role="alert">{problem}</Banner>}
      {/* Welcome has one button and nothing to save: no bar around it. */}
      <div className={cx('z-20 mt-2 flex flex-wrap items-center gap-3', step > 0 && 'rounded-card border border-line bg-white px-4 py-3.5 shadow-bar sm:sticky sm:bottom-4 sm:px-5 sm:py-4')}>
        {step >= 1 && <Button variant="link" className="!px-3 !text-[15px]" onClick={() => go(step - 1)}>Back</Button>}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
          {step >= 1 && editable && (
            <Button variant="secondary" onClick={() => void saveLater()} disabled={busy}>Save and finish later</Button>
          )}
          <Button variant="primary" size="lg" onClick={() => void next()} disabled={busy} aria-busy={busy || undefined}>
            {label}
          </Button>
        </div>
      </div>
    </IntakeLayout>
  );
}
